// Code 128 (ISO/IEC 15417) rendered as SVG, plus a printable label sheet.
// Code set C packs digit pairs (SSCCs, numeric LPNs); code set B covers ASCII.
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { api } from '../api';
import { Button, Skeleton } from './kit';
import { Dialog } from './overlays';

const PATTERNS = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
];
const STOP = '2331112';

/** Bar/space module widths for `text` (Code 128 B, or C for even-length digit strings). */
export function code128(text: string): number[] {
  const useC = /^\d+$/.test(text) && text.length % 2 === 0 && text.length >= 4;
  const values: number[] = [useC ? 105 : 104];
  if (useC) for (let i = 0; i < text.length; i += 2) values.push(Number(text.slice(i, i + 2)));
  else
    for (const ch of text) {
      const c = ch.charCodeAt(0);
      values.push(c >= 32 && c <= 127 ? c - 32 : 0);
    }
  const check = values.reduce((sum, v, i) => sum + v * (i === 0 ? 1 : i), 0) % 103;
  values.push(check);
  const widths: number[] = [];
  for (const v of values) for (const d of PATTERNS[v]!) widths.push(Number(d));
  for (const d of STOP) widths.push(Number(d));
  return widths;
}

export function Barcode({
  value,
  height = 64,
  module = 2,
  showText = true,
}: {
  value: string;
  height?: number;
  module?: number;
  showText?: boolean;
}) {
  const widths = code128(value);
  const quiet = 10;
  const total = widths.reduce((a, b) => a + b, 0) + quiet * 2;
  let x = quiet;
  const bars: { x: number; w: number }[] = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push({ x, w });
    x += w;
  });
  const textH = showText ? 18 : 0;
  return (
    <svg
      role="img"
      aria-label={`Barcode ${value}`}
      width={total * module}
      height={height + textH}
      viewBox={`0 0 ${total} ${height + textH}`}
      preserveAspectRatio="none"
      style={{ background: '#fff', maxWidth: '100%' }}
    >
      {bars.map((b) => (
        <rect key={b.x} x={b.x} y={0} width={b.w} height={height} fill="#000" />
      ))}
      {showText ? (
        <text
          x={total / 2}
          y={height + 14}
          textAnchor="middle"
          fontFamily="ui-monospace, monospace"
          fontSize={12}
          fill="#000"
        >
          {value}
        </text>
      ) : null}
    </svg>
  );
}

interface LpnLabel {
  code: string;
  type: string;
  locationCode: string | null;
  contents: {
    sku: string;
    description: string;
    lot: string | null;
    expiry: string | null;
    qty: number;
  }[];
  zpl: string;
}

/** Opens the browser print dialog with only the given element's markup. */
export function printElement(id: string, title: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const w = window.open('', '_blank', 'width=600,height=800');
  if (!w) return;
  w.document.title = title;
  const style = w.document.createElement('style');
  style.textContent = `body{font-family:Inter,system-ui,sans-serif;margin:0;padding:16px;color:#000}
    .label{width:4in;min-height:5.5in;border:1px solid #000;padding:14px;box-sizing:border-box;page-break-after:always}
    .label h3{margin:0 0 6px;font-size:14px;letter-spacing:.08em}
    .label .big{font-size:22px;font-weight:700;margin:10px 0 2px}
    .label .meta{font-size:12px;margin:2px 0}
    svg{max-width:100%}
    .doc{font-size:12px}.doc-table{width:100%;border-collapse:collapse}
    .doc-table th,.doc-table td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top}
    .doc-table th{background:#f2f2f2}
    ${id.startsWith('lpn') ? '@page{size:4in 6in;margin:0.2in}' : '@page{size:letter;margin:0.5in}'}`;
  w.document.head.appendChild(style);
  w.document.body.innerHTML = el.innerHTML;
  w.focus();
  w.print();
  setTimeout(() => w.close(), 500);
}

export function LpnLabelDialog({ lpnId, onClose }: { lpnId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['labels', lpnId],
    queryFn: () => api<LpnLabel>(`/labels/lpns/${lpnId}`),
  });
  const l = q.data;
  const first = l?.contents[0];
  return (
    <Dialog
      title="LPN label"
      description="4 × 6 in thermal label. Zebra printers can take the ZPL directly."
      onClose={onClose}
      footer={
        <>
          {l ? (
            <Button onClick={() => void navigator.clipboard.writeText(l.zpl)}>Copy ZPL</Button>
          ) : null}
          <Button
            variant="primary"
            icon={Printer}
            disabled={!l}
            onClick={() => printElement('lpn-label-print', `LPN ${l?.code ?? ''}`)}
          >
            Print
          </Button>
        </>
      }
    >
      {!l ? (
        <Skeleton height={260} />
      ) : (
        <div id="lpn-label-print">
          <div
            className="label"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 16,
              background: '#fff',
              color: '#000',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 13, letterSpacing: '0.08em' }}>
              LICENSE PLATE · {l.type.toUpperCase()}
            </h3>
            <div style={{ margin: '12px 0' }}>
              <Barcode value={l.code} height={90} />
            </div>
            {first ? (
              <>
                <div className="big" style={{ fontSize: 20, fontWeight: 700 }}>
                  {first.sku} × {first.qty}
                </div>
                <div className="meta">{first.description}</div>
                {first.lot ? (
                  <div className="meta">
                    LOT {first.lot}
                    {first.expiry ? ` · EXP ${first.expiry}` : ''}
                  </div>
                ) : null}
                {l.contents.length > 1 ? (
                  <div className="meta">+ {l.contents.length - 1} more SKU(s)</div>
                ) : null}
              </>
            ) : (
              <div className="meta">Empty</div>
            )}
            {l.locationCode ? <div className="meta">At {l.locationCode}</div> : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}
